// 2026-05-01 P2 fix: dedupe scroll listener via named handler + cache parallax inner element in WeakMap on IO add (P2 items #1, #2).
/* KIC motion primitives - Wave D 2026-04-30
 *
 * Vanilla, no dependencies. Honors prefers-reduced-motion: reduce.
 * Three primitives, scoped via [data-io] attributes:
 *
 *   data-io="reveal"   single-element fade + translate-Y on scroll into view
 *   data-io="stagger"  parent. Children reveal sequentially (60-100ms apart)
 *   data-io="parallax" hero media. Subtle translate3d on scroll (max ~40px)
 *
 * One-shot for reveal/stagger (unobserve after first trigger). Parallax
 * runs continuously while in viewport via rAF + scroll listener.
 *
 * CSS pairs: see kic-v2.css "Wave D motion" block.
 */
(function () {
  'use strict';

  var doc = document;
  var win = window;

  // Reduced-motion gate: full short-circuit, no observers wired.
  var prefersReducedMotion = win.matchMedia && win.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReducedMotion) {
    // Mark every motion target as already-revealed so any opacity:0 base state lifts.
    doc.querySelectorAll('[data-io]').forEach(function (el) {
      el.setAttribute('data-io-revealed', '');
    });
    return;
  }

  // ---------- Reveal + stagger (IntersectionObserver) ----------
  if ('IntersectionObserver' in win) {
    var revealIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var mode = el.getAttribute('data-io');
        if (mode === 'stagger') {
          var children = el.children;
          var max = children.length;
          // Cap visible-stagger window: even on long rails, top 10 stagger; rest reveal in one batch.
          var staggerCap = 10;
          var step = 70; // ms between children
          for (var i = 0; i < max; i++) {
            var child = children[i];
            var delay = i < staggerCap ? i * step : staggerCap * step;
            child.style.transitionDelay = delay + 'ms';
            child.setAttribute('data-io-revealed', '');
          }
        } else {
          el.setAttribute('data-io-revealed', '');
        }
        revealIO.unobserve(el);
      });
    }, {
      threshold: 0.12,
      rootMargin: '0px 0px -8% 0px'
    });

    doc.querySelectorAll('[data-io="reveal"], [data-io="stagger"]').forEach(function (el) {
      revealIO.observe(el);
    });
  } else {
    // Old browser fallback: reveal everything immediately.
    doc.querySelectorAll('[data-io]').forEach(function (el) {
      el.setAttribute('data-io-revealed', '');
    });
  }

  // ---------- Parallax (rAF + scroll, only while in viewport) ----------
  var parallaxNodes = Array.prototype.slice.call(doc.querySelectorAll('[data-io="parallax"]'));
  if (parallaxNodes.length && 'IntersectionObserver' in win) {
    var active = new Set();
    var raf = null;
    var lastScrollY = win.scrollY;
    // P2 fix #2: cache inner reference per node so rAF tick does not re-query DOM each frame.
    var innerCache = new WeakMap();
    function getInner(node) {
      var cached = innerCache.get(node);
      if (cached && node.contains(cached)) return cached;
      var inner = node.querySelector('[data-io-parallax-target]') || node;
      innerCache.set(node, inner);
      return inner;
    }
    // P2 fix #1: explicit listener-attached guard so we never call addEventListener
    // more than once per binding cycle (spec dedupes identical handler+capture, but
    // an explicit guard is auditable + immune to options-object identity drift).
    var scrollListenerAttached = false;

    function update() {
      raf = null;
      active.forEach(function (node) {
        var rect = node.getBoundingClientRect();
        var vh = win.innerHeight || doc.documentElement.clientHeight;
        // Progress -1..1 across viewport pass
        var center = rect.top + rect.height / 2;
        var progress = (center - vh / 2) / (vh / 2 + rect.height / 2);
        // Clamp
        if (progress < -1) progress = -1;
        if (progress > 1) progress = 1;
        var maxOffset = parseFloat(node.getAttribute('data-io-parallax-max') || '28');
        // Smooth ease-out remap of linear progress (-1..1). Cubic so the
        // translate slows as it approaches its bounds rather than hitting
        // them linearly. Reads more natural on slow scrolls. Sign-preserving.
        var sign = progress < 0 ? -1 : 1;
        var abs = progress < 0 ? -progress : progress;
        var eased = sign * (1 - Math.pow(1 - abs, 3));
        // Inverse: as you scroll down, image translates UP a touch (classic parallax)
        var ty = -eased * maxOffset;
        var inner = getInner(node);
        // 1.06× scale buffer so translate never exposes background.
        inner.style.transform = 'translate3d(0,' + ty.toFixed(2) + 'px,0) scale(1.06)';
      });
    }

    function onScroll() {
      if (raf == null) {
        raf = win.requestAnimationFrame(update);
      }
    }

    var pxIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          active.add(entry.target);
          // Warm the cache on entry so first rAF tick is already cached.
          getInner(entry.target);
        } else {
          active.delete(entry.target);
          var inner = getInner(entry.target);
          // Preserve the 1.06× scale buffer when out of viewport so re-entry
          // doesn't pop. Translate resets to 0; scale persists.
          inner.style.transform = 'translate3d(0,0,0) scale(1.06)';
        }
      });
      if (active.size > 0 && !scrollListenerAttached) {
        win.addEventListener('scroll', onScroll, { passive: true });
        scrollListenerAttached = true;
        update();
      } else if (active.size === 0 && scrollListenerAttached) {
        win.removeEventListener('scroll', onScroll, { passive: true });
        scrollListenerAttached = false;
      }
    }, { threshold: 0 });

    parallaxNodes.forEach(function (node) { pxIO.observe(node); });
  }
})();
